import Img from '../components/Img';
import React, { useState, useEffect } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useNavigate } from 'react-router-dom';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { getStoredAttribution } from '../attribution';

// Declare window interfaces for tracking
declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom',
      eventName: string,
      parameters?: Record<string, any>
    ) => void;
    dataLayer?: any[];
    // Set to true only inside the build-time prerenderer (prerender.mjs) so
    // components can skip live data fetches when snapshotting the static shell.
    __PRERENDER__?: boolean;
  }
}

const SMS_NUMBER = '5082031342';
const DISPLAY_NUMBER = '508-203-1342';
const AVAILABILITY_API = 'https://tattooremoval-crm.com/api/external/consultation-availability/atomictattooremoval';
const SAME_DAY_BOOKING_CUTOFF_HOURS = 3;

// ──────────────────────────────────────────────────────────────
// PHONE MODAL
// ──────────────────────────────────────────────────────────────

const PhoneModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: 16, padding: '32px 40px',
        textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        maxWidth: 340, width: '90%',
      }}
    >
      <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5C96AC', marginBottom: 8 }}>
        Text Us
      </p>
      <p style={{ fontSize: 'clamp(1.6rem, 6vw, 2.2rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>
        {DISPLAY_NUMBER}
      </p>
      <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
        Text us your availability for a free consultation and we'll help you find the best time for your appointment.
      </p>
      <button
        onClick={onClose}
        style={{
          background: '#cb4f2e', color: '#fff', border: 'none', borderRadius: 50,
          padding: '10px 28px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
        }}
      >
        Close
      </button>
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ──────────────────────────────────────────────────────────────

interface TimeRange {
  start: Date;
  end: Date;
}

interface WeeklyDayHours {
  enabled: boolean;
  start: string; // "09:30"
  end: string;   // "18:00"
}

interface WeeklyHours {
  monday: WeeklyDayHours;
  tuesday: WeeklyDayHours;
  wednesday: WeeklyDayHours;
  thursday: WeeklyDayHours;
  friday: WeeklyDayHours;
  saturday: WeeklyDayHours;
  sunday: WeeklyDayHours;
}

interface UnavailablePeriod {
  id?: string;
  date: string;
  type: "full_day" | "partial_day";
  start?: string;
  end?: string;
  reason?: string;
}

interface ConsultationAvailabilityResponse {
  tenantId: string;
  timezone: string;
  consultationDurationMinutes: number;
  weeklyHours: WeeklyHours;
  unavailablePeriods: UnavailablePeriod[];
}

interface BusyTime {
  start: string; // ISO string
  end: string;   // ISO string
}

interface ConsultationSlot {
  dateTime: string;
  displayTime: string;
  displayDate: string;
}

interface BookingFormData {
  name: string;
  phone: string;
  email: string;
  selectedDateTime: string;
}

// ──────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ──────────────────────────────────────────────────────────────

/**
 * Format phone number as user types
 */
function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
}

/**
 * Parse time string "HH:MM" and set it on a date
 */
function setTimeOnDate(date: Date, timeStr: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
}

/**
 * Subtract busy times from available time ranges
 */
function subtractTimeRanges(available: TimeRange[], busy: TimeRange[]): TimeRange[] {
  let result = [...available];
  
  for (const busyRange of busy) {
    const newResult: TimeRange[] = [];
    
    for (const availRange of result) {
      // No overlap
      if (busyRange.end <= availRange.start || busyRange.start >= availRange.end) {
        newResult.push(availRange);
        continue;
      }
      
      // Busy range completely covers available range
      if (busyRange.start <= availRange.start && busyRange.end >= availRange.end) {
        continue; // Skip this range entirely
      }
      
      // Busy range is in the middle
      if (busyRange.start > availRange.start && busyRange.end < availRange.end) {
        newResult.push({ start: availRange.start, end: busyRange.start });
        newResult.push({ start: busyRange.end, end: availRange.end });
        continue;
      }
      
      // Busy range overlaps start
      if (busyRange.start <= availRange.start && busyRange.end < availRange.end) {
        newResult.push({ start: busyRange.end, end: availRange.end });
        continue;
      }
      
      // Busy range overlaps end
      if (busyRange.start > availRange.start && busyRange.end >= availRange.end) {
        newResult.push({ start: availRange.start, end: busyRange.start });
        continue;
      }
    }
    
    result = newResult;
  }
  
  return result;
}

/**
 * Generate available time slots for a specific date using timezone-aware date handling
 */
function generateAvailableSlots({
  date,
  weeklyHours,
  unavailablePeriods,
  busyTimes,
  consultationDurationMinutes,
  timezone,
}: {
  date: Date;
  weeklyHours: WeeklyHours;
  unavailablePeriods: UnavailablePeriod[];
  busyTimes: BusyTime[];
  consultationDurationMinutes: number;
  timezone: string;
}): Date[] {
  // Step 1: Get day of week in the CRM timezone
  const dayName = format(toZonedTime(date, timezone), 'EEEE').toLowerCase() as keyof WeeklyHours;
  
  // Step 2: Check if enabled
  const dayHours = weeklyHours[dayName];
  if (!dayHours || !dayHours.enabled) {
    return [];
  }
  
  // Step 3: Build base working window in the CRM timezone
  const workStart = setTimeOnDate(date, dayHours.start);
  const workEnd = setTimeOnDate(date, dayHours.end);
  
  let availableRanges: TimeRange[] = [{ start: workStart, end: workEnd }];
  
  // Step 4: Remove unavailable periods from CRM
  const dateStr = format(toZonedTime(date, timezone), 'yyyy-MM-dd');
  const unavailableForDay = unavailablePeriods.filter(
    (p) => p.date === dateStr
  );
  
  for (const period of unavailableForDay) {
    if (period.type === 'full_day') {
      return []; // Entire day unavailable
    }
    
    if (period.type === 'partial_day' && period.start && period.end) {
      const unavailStart = setTimeOnDate(date, period.start);
      const unavailEnd = setTimeOnDate(date, period.end);
      availableRanges = subtractTimeRanges(availableRanges, [
        { start: unavailStart, end: unavailEnd },
      ]);
    }
  }
  
  // Step 5: Remove Google Calendar busy times (no buffer needed)
  // The consultation duration already prevents overlaps since each slot
  // is exactly one consultation duration long
  const busyRanges: TimeRange[] = busyTimes.map((bt) => ({
    start: new Date(bt.start),
    end: new Date(bt.end),
  }));
  
  availableRanges = subtractTimeRanges(availableRanges, busyRanges);
  
  // Step 6: Split into slots with same-day cutoff
  const slots: Date[] = [];
  const slotDuration = consultationDurationMinutes * 60 * 1000; // milliseconds
  const now = new Date();
  const nowInTimezone = toZonedTime(now, timezone);
  const cutoffMilliseconds = SAME_DAY_BOOKING_CUTOFF_HOURS * 60 * 60 * 1000;
  
  for (const range of availableRanges) {
    let current = new Date(range.start);
    
    while (current.getTime() + slotDuration <= range.end.getTime()) {
      // Check if slot is in the future with proper cutoff time
      const slotInTimezone = toZonedTime(current, timezone);
      const isSameDay = format(slotInTimezone, 'yyyy-MM-dd') === format(nowInTimezone, 'yyyy-MM-dd');
      
      if (isSameDay) {
        // For same-day slots, require SAME_DAY_BOOKING_CUTOFF_HOURS
        if (current.getTime() > now.getTime() + cutoffMilliseconds) {
          slots.push(new Date(current));
        }
      } else {
        // For future-day slots, just need to be after now
        if (current.getTime() > now.getTime()) {
          slots.push(new Date(current));
        }
      }
      
      current = new Date(current.getTime() + slotDuration);
    }
  }
  
  return slots;
}

const Booking: React.FC = () => {
  const navigate = useNavigate();
  
  // Availability configuration
  const [availabilityConfig, setAvailabilityConfig] = useState<ConsultationAvailabilityResponse | null>(null);
  
  // Modal and mobile detection
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Date selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  
  // Time slots for selected date
  const [availableSlots, setAvailableSlots] = useState<ConsultationSlot[]>([]);
  
  // Loading states
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [crmEndpointFailed, setCrmEndpointFailed] = useState(false);
  const [calendarVerificationFailed, setCalendarVerificationFailed] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    phone: '',
    email: '',
    selectedDateTime: '',
  });
  const [formTouched, setFormTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Detect mobile device
  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Fetch availability configuration on mount
  useEffect(() => {
    fetchAvailabilityConfig();
  }, []);

  // Generate available dates when config loads
  useEffect(() => {
    if (availabilityConfig) {
      generateAvailableDates();
    }
  }, [availabilityConfig]);

  // Generate time slots when date is selected
  useEffect(() => {
    if (selectedDate && availabilityConfig) {
      generateTimeSlotsForDate(selectedDate);
    }
  }, [selectedDate, availabilityConfig]);

  /**
   * Fetch consultation availability configuration
   */
  const fetchAvailabilityConfig = async () => {
    // During static prerender there is no backend, and we don't want build-time
    // dates baked into the snapshot. Leave the slot picker in its loading state;
    // the real client fetches availability on hydration.
    if (typeof window !== 'undefined' && window.__PRERENDER__) return;
    try {
      const response = await fetch(AVAILABILITY_API);
      if (!response.ok) {
        throw new Error('CRM availability endpoint failed');
      }
      const data: ConsultationAvailabilityResponse = await response.json();
      setAvailabilityConfig(data);
      setCrmEndpointFailed(false);
    } catch (err) {
      console.error('Error fetching CRM availability config:', err);
      // DO NOT set fallback configuration - show error message instead
      setAvailabilityConfig(null);
      setCrmEndpointFailed(true);
      setError('Unable to load appointment times right now. Please text us to book.');
    } finally {
      setLoadingConfig(false);
    }
  };

  /**
   * Fetch Google Calendar busy times for a date range
   */
  const fetchGoogleCalendarBusyTimes = async (startDate: Date, endDate: Date): Promise<BusyTime[]> => {
    try {
      const response = await fetch('/api/calendar-busy-times', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timeMin: startDate.toISOString(),
          timeMax: endDate.toISOString(),
        }),
      });
      
      if (!response.ok) {
        throw new Error('Calendar verification failed');
      }
      
      const data = await response.json();
      return data.busyTimes || [];
    } catch (err) {
      console.error('Error fetching Google Calendar busy times:', err);
      // Throw error to prevent showing slots without verification
      throw new Error('Calendar verification failed');
    }
  };

  /**
   * Generate list of available dates (next 14 days with enabled weekdays)
   */
  const generateAvailableDates = () => {
    if (!availabilityConfig) return;
    
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof WeeklyHours;
      const dayHours = availabilityConfig.weeklyHours[dayName];
      
      if (dayHours && dayHours.enabled) {
        // Check if not fully unavailable
        const dateStr = date.toISOString().split('T')[0];
        const isFullyUnavailable = availabilityConfig.unavailablePeriods.some(
          (p) => p.date === dateStr && p.type === 'full_day'
        );
        
        if (!isFullyUnavailable) {
          dates.push(date);
        }
      }
    }
    
    setAvailableDates(dates);
    
    // Auto-select first available date
    if (dates.length > 0 && !selectedDate) {
      setSelectedDate(dates[0]);
    }
  };

  /**
   * Generate time slots for a specific date
   */
  const generateTimeSlotsForDate = async (date: Date) => {
    if (!availabilityConfig) return;
    
    setLoadingSlots(true);
    setError('');
    setCalendarVerificationFailed(false);
    
    try {
      // Fetch busy times for a 1-day window
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);
      
      const busy = await fetchGoogleCalendarBusyTimes(dayStart, dayEnd);
      
      // Generate available slots using timezone
      const slots = generateAvailableSlots({
        date,
        weeklyHours: availabilityConfig.weeklyHours,
        unavailablePeriods: availabilityConfig.unavailablePeriods,
        busyTimes: busy,
        consultationDurationMinutes: availabilityConfig.consultationDurationMinutes,
        timezone: availabilityConfig.timezone,
      });
      
      // Format slots for display using CRM timezone
      const formattedSlots: ConsultationSlot[] = slots.map((slot) => {
        const slotInTimezone = toZonedTime(slot, availabilityConfig.timezone);
        return {
          dateTime: slot.toISOString(),
          displayTime: format(slotInTimezone, 'h:mm a'),
          displayDate: format(slotInTimezone, 'EEE, MMM d'),
        };
      });
      
      setAvailableSlots(formattedSlots);
      setCalendarVerificationFailed(false);
    } catch (err) {
      console.error('Error generating slots:', err);
      // Show specific error message for calendar failures
      if (err instanceof Error && err.message.includes('Calendar verification failed')) {
        setCalendarVerificationFailed(true);
        setError('Unable to verify available appointment times right now. Please text us to book.');
      } else {
        setError('Unable to load available times for this date. Please text us to book.');
      }
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Track when user starts interacting with booking form
  const handleFormInteraction = () => {
    if (!formTouched) {
      setFormTouched(true);
      // Fire lower-intent event when user starts booking
      if (window.fbq) {
        window.fbq('trackCustom', 'BookingStarted');
      }
    }
  };

  // Handle SMS link click
  const handleSmsClick = () => {
    if (window.fbq) {
      window.fbq('track', 'Contact');
    }
  };

  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Format phone number as user types
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    handleFormInteraction();
  };

  // Handle time slot selection
  const handleSlotSelection = (dateTime: string) => {
    setFormData(prev => ({ ...prev, selectedDateTime: dateTime }));
    handleFormInteraction();
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.selectedDateTime) {
      setError('Please select a consultation time');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Flat UTM params from the CURRENT url — kept for the Google Calendar
      // event description (existing behavior).
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_content: urlParams.get('utm_content') || '',
        utm_term: urlParams.get('utm_term') || '',
      };

      // Full marketing attribution captured at LANDING (survives navigation),
      // forwarded to the CRM via our own backend. Posting to /api/book-consultation
      // (NOT directly to the CRM) keeps the CRM token server-side.
      const response = await fetch('/api/book-consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          sourcePage: window.location.href,
          ...utmParams,
          attribution: getStoredAttribution(),
          tracking: {
            metaEventName: 'Schedule',
            ga4EventName: 'book_consultation',
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Booking submission failed');
      }

      // Success! Fire tracking events
      // Meta Pixel conversion event
      if (window.fbq) {
        window.fbq('track', 'Schedule');
      }
      
      // GTM conversion event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'book_consultation',
          event_category: 'booking',
          event_label: 'Free Consultation',
        });
      }

      // Redirect to thank-you page
      navigate('/book-consultation/thank-you', { 
        state: { 
          name: formData.name,
          dateTime: formData.selectedDateTime 
        } 
      });
    } catch (err) {
      console.error('Booking error:', err);
      
      // Check for specific error types
      if (err instanceof Error) {
        // Handle slot conflict - refresh the slots
        if (err.message.includes('no longer available') || err.message.includes('Booking conflict')) {
          setError(err.message);
          // Refresh slots to show updated availability
          if (selectedDate) {
            generateTimeSlotsForDate(selectedDate);
          }
        } else if (err.message.includes('temporarily unavailable') || err.message.includes('Service configuration')) {
          setError('Booking service is temporarily unavailable. Please text us your availability.');
        } else {
          setError('An error occurred while booking. Please try again or text us your availability.');
        }
      } else {
        setError('An error occurred while booking. Please text us your availability.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showPhoneModal && <PhoneModal onClose={() => setShowPhoneModal(false)} />}
      <Helmet>
        <title>Free Laser Tattoo Removal Consultation | Atomic Tattoo Removal</title>
        <meta
          name="description"
          content="Get a free laser tattoo removal consultation at Atomic Tattoo Removal in Wrentham, MA. Text us your availability and we'll find a time that works for you."
        />
        <meta
          name="keywords"
          content="free tattoo removal consultation, laser tattoo removal consultation, tattoo removal pricing, how many sessions, Atomic Tattoo Removal, Wrentham MA"
        />
      </Helmet>

      <div
        className="booking-page"
        style={{
          maxWidth: 680,
          margin: '0 auto',
          padding: '24px 16px 48px',
        }}
      >
        {/* ── Hero: Title + Trust Photo ── */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1
            style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: 800,
              color: '#1e3a5f',
              marginBottom: 6,
            }}
          >
            Free Tattoo Removal Consultation
          </h1>
          <p
            style={{
              color: '#5C96AC',
              fontSize: '1rem',
              fontWeight: 600,
              marginBottom: 20,
            }}
          >
            Laser Tattoo Removal Consultation — Wrentham, MA
          </p>

          {/* Team photo — builds trust and reduces anxiety */}
          <Img
            src="/assets/staff.png"
            alt="The Atomic Tattoo Removal team — three friendly laser removal technicians"
            style={{
              // Sized by width so the browser reserves space before load (no
              // layout shift / CLS); height follows the aspect ratio. maxWidth
              // 610 ≈ 320px tall on desktop for this image (height = width / 1.9).
              width: '100%',
              maxWidth: 610,
              height: 'auto',
              borderRadius: 16,
              boxShadow: '0 6px 24px rgba(30,58,95,0.14)',
              display: 'block',
              margin: '0 auto 16px',
            }}
          />
          <p
            style={{
              color: '#777',
              fontSize: '0.88rem',
              fontStyle: 'italic',
              marginBottom: 12,
            }}
          >
            Meet our team — safe, effective, and supportive laser tattoo removal.
          </p>
          <p
            style={{
              display: 'inline-block',
              background: '#f0f7fa',
              border: '1px solid #c2dae6',
              borderRadius: 8,
              padding: '8px 16px',
              color: '#2d5986',
              fontSize: '0.88rem',
              fontWeight: 600,
            }}
          >
            Individually owned and operated — not a franchise or corporate chain.
          </p>
        </div>

        {/* ── PRIMARY CTA: Text Us Your Availability ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)',
            borderRadius: 20,
            padding: 'clamp(28px, 5vw, 44px) clamp(20px, 5vw, 40px)',
            textAlign: 'center',
            marginBottom: 32,
            boxShadow: '0 6px 24px rgba(30,58,95,0.20)',
          }}
        >
          <p
            style={{
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#90c8e0',
              marginBottom: 10,
            }}
          >
            Fastest Way to Get Started
          </p>
          <h2
            style={{
              fontSize: 'clamp(1.5rem, 5vw, 2.2rem)',
              fontWeight: 800,
              color: '#fff',
              marginBottom: 16,
              lineHeight: 1.2,
            }}
          >
            Text Us Your Availability
          </h2>
          <p
            style={{
              color: '#d0e8f5',
              fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)',
              maxWidth: 480,
              margin: '0 auto 28px',
              lineHeight: 1.7,
            }}
          >
            Text us your availability for a free consultation and we'll help you find
            the best time for your appointment. You can also{' '}
            <strong style={{ color: '#fff' }}>text a photo of your tattoo</strong>{' '}
            and ask questions about{' '}
            <strong style={{ color: '#fff' }}>pricing</strong>,{' '}
            <strong style={{ color: '#fff' }}>fading expectations</strong>, and{' '}
            <strong style={{ color: '#fff' }}>number of sessions</strong>.
          </p>

          <p
            style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 8,
              padding: '10px 18px',
              color: '#e8f4fb',
              fontSize: '0.9rem',
              fontStyle: 'italic',
              marginBottom: 24,
            }}
          >
            All texts are answered directly by owner Craig Howard — not a robot 🤖 or call center.
          </p>

          {isMobile ? (
            <a
              href={`sms:${SMS_NUMBER}`}
              onClick={handleSmsClick}
              style={{
                display: 'inline-block',
                background: '#cb4f2e',
                color: '#fff',
                fontWeight: 800,
                fontSize: 'clamp(1.05rem, 3vw, 1.25rem)',
                padding: '18px 48px',
                borderRadius: 50,
                textDecoration: 'none',
                letterSpacing: '0.02em',
                boxShadow: '0 4px 18px rgba(203,79,46,0.45)',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#a83c22')}
              onMouseOut={e => (e.currentTarget.style.background = '#cb4f2e')}
            >
              Text Us Now
            </a>
          ) : (
            <button
              onClick={() => { handleSmsClick(); setShowPhoneModal(true); }}
              style={{
                display: 'inline-block',
                background: '#cb4f2e',
                color: '#fff',
                fontWeight: 800,
                fontSize: 'clamp(1.05rem, 3vw, 1.25rem)',
                padding: '18px 48px',
                borderRadius: 50,
                border: 'none',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                boxShadow: '0 4px 18px rgba(203,79,46,0.45)',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#a83c22')}
              onMouseOut={e => (e.currentTarget.style.background = '#cb4f2e')}
            >
              Text Us Now
            </button>
          )}

          <p
            style={{
              color: '#90c8e0',
              fontSize: '0.9rem',
              marginTop: 14,
            }}
          >
            {DISPLAY_NUMBER}
          </p>
        </div>

        {/* ── Divider ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 32,
          }}
        >
          <div style={{ flex: 1, height: 1, background: '#dde4ec' }} />
          <span style={{ color: '#888', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            OR
          </span>
          <div style={{ flex: 1, height: 1, background: '#dde4ec' }} />
        </div>

        {/* ── SECONDARY CTA: Book Online ── */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h2
            style={{
              fontSize: 'clamp(1.3rem, 4vw, 1.75rem)',
              fontWeight: 700,
              color: '#1e3a5f',
              marginBottom: 8,
            }}
          >
            Book Your Free Consultation
          </h2>
          <p
            style={{
              color: '#555',
              fontSize: '1rem',
              marginBottom: 8,
              lineHeight: 1.6,
            }}
          >
            Choose a time that works for you. Your consultation is free, and we'll review
            your tattoo, skin type, goals, and estimated treatment plan.
          </p>
        </div>

            {error && (
              <div
                style={{
                  background: '#fff3cd',
                  border: '2px solid #cb4f2e',
                  borderRadius: 12,
                  padding: '16px',
                  marginBottom: 20,
                  color: '#856404',
                  fontSize: '0.95rem',
                }}
              >
                {error}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              style={{
                background: '#f8fbfd',
                border: '2px solid #c2dae6',
                borderRadius: 16,
                padding: 'clamp(24px, 5vw, 32px)',
                marginBottom: 32,
              }}
            >
              {/* Name Field */}
              <div style={{ marginBottom: 20 }}>
                <label
                  htmlFor="name"
                  style={{
                    display: 'block',
                    color: '#1e3a5f',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    marginBottom: 8,
                  }}
                >
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '1rem',
                    border: '2px solid #dde4ec',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                  placeholder="Jane Smith"
                />
              </div>

              {/* Phone Field */}
              <div style={{ marginBottom: 20 }}>
                <label
                  htmlFor="phone"
                  style={{
                    display: 'block',
                    color: '#1e3a5f',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    marginBottom: 8,
                  }}
                >
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '1rem',
                    border: '2px solid #dde4ec',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Email Field */}
              <div style={{ marginBottom: 20 }}>
                <label
                  htmlFor="email"
                  style={{
                    display: 'block',
                    color: '#1e3a5f',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    marginBottom: 8,
                  }}
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '1rem',
                    border: '2px solid #dde4ec',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                  placeholder="jane@example.com"
                />
              </div>

              {/* Date Picker */}
              <div style={{ marginBottom: 24 }}>
                <label
                  style={{
                    display: 'block',
                    color: '#1e3a5f',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    marginBottom: 12,
                  }}
                >
                  Select Date *
                </label>

                {loadingConfig ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '24px',
                      color: '#5C96AC',
                    }}
                  >
                    Loading available dates...
                  </div>
                ) : crmEndpointFailed ? (
                  <div
                    style={{
                      background: '#fff3cd',
                      border: '2px solid #cb4f2e',
                      borderRadius: 8,
                      padding: '16px',
                      textAlign: 'center',
                      color: '#856404',
                      fontSize: '0.9rem',
                    }}
                  >
                    Unable to load appointment times right now. Please text us to book.
                  </div>
                ) : availableDates.length === 0 ? (
                  <div
                    style={{
                      background: '#fff',
                      border: '2px dashed #c2dae6',
                      borderRadius: 8,
                      padding: '16px',
                      textAlign: 'center',
                      color: '#555',
                      fontSize: '0.9rem',
                    }}
                  >
                    No dates currently available online. Please text us your availability.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {availableDates.map((date, index) => {
                      const isSelected =
                        selectedDate?.toDateString() === date.toDateString();
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setSelectedDate(date);
                            setFormData((prev) => ({ ...prev, selectedDateTime: '' }));
                            handleFormInteraction();
                          }}
                          style={{
                            background: isSelected
                              ? 'linear-gradient(135deg, #2d5986 0%, #1e3a5f 100%)'
                              : '#fff',
                            color: isSelected ? '#fff' : '#1e3a5f',
                            border: isSelected
                              ? '2px solid #2d5986'
                              : '2px solid #dde4ec',
                            borderRadius: 8,
                            padding: '10px 6px',
                            fontSize: '0.85rem',
                            fontWeight: isSelected ? 700 : 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            textAlign: 'center',
                          }}
                          onMouseOver={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#5C96AC';
                              e.currentTarget.style.background = '#f0f7fa';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#dde4ec';
                              e.currentTarget.style.background = '#fff';
                            }
                          }}
                        >
                          <div style={{ fontSize: '0.75rem', marginBottom: 2 }}>
                            {date.toLocaleDateString('en-US', { weekday: 'short' })}
                          </div>
                          <div style={{ fontWeight: 700 }}>
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Available Time Slots */}
              <div style={{ marginBottom: 24 }}>
                <label
                  style={{
                    display: 'block',
                    color: '#1e3a5f',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    marginBottom: 12,
                  }}
                >
                  Select Consultation Time *
                </label>

                {!selectedDate ? (
                  <div
                    style={{
                      background: '#f0f7fa',
                      border: '2px dashed #c2dae6',
                      borderRadius: 8,
                      padding: '20px',
                      textAlign: 'center',
                      color: '#5C96AC',
                      fontSize: '0.9rem',
                    }}
                  >
                    Please select a date first
                  </div>
                ) : loadingSlots ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '32px',
                      color: '#5C96AC',
                    }}
                  >
                    Loading available times...
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div
                    style={{
                      background: '#fff',
                      border: '2px dashed #c2dae6',
                      borderRadius: 8,
                      padding: '20px',
                      textAlign: 'center',
                      color: '#555',
                    }}
                  >
                    <p style={{ margin: '0 0 12px', fontWeight: 600 }}>
                      No available times for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>
                      Please select another date or text us your availability.
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.dateTime}
                        type="button"
                        onClick={() => handleSlotSelection(slot.dateTime)}
                        style={{
                          background:
                            formData.selectedDateTime === slot.dateTime
                              ? 'linear-gradient(135deg, #2d5986 0%, #1e3a5f 100%)'
                              : '#fff',
                          color:
                            formData.selectedDateTime === slot.dateTime ? '#fff' : '#1e3a5f',
                          border:
                            formData.selectedDateTime === slot.dateTime
                              ? '2px solid #2d5986'
                              : '2px solid #dde4ec',
                          borderRadius: 8,
                          padding: '14px 8px',
                          fontSize: '0.95rem',
                          fontWeight: formData.selectedDateTime === slot.dateTime ? 700 : 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          textAlign: 'center',
                        }}
                        onMouseOver={(e) => {
                          if (formData.selectedDateTime !== slot.dateTime) {
                            e.currentTarget.style.borderColor = '#5C96AC';
                            e.currentTarget.style.background = '#f0f7fa';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (formData.selectedDateTime !== slot.dateTime) {
                            e.currentTarget.style.borderColor = '#dde4ec';
                            e.currentTarget.style.background = '#fff';
                          }
                        }}
                      >
                        {slot.displayTime}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={
                  submitting || 
                  !formData.name.trim() || 
                  !formData.phone.trim() || 
                  !formData.email.trim() || 
                  !formData.selectedDateTime || 
                  crmEndpointFailed || 
                  calendarVerificationFailed
                }
                style={{
                  width: '100%',
                  background: (
                    submitting || 
                    !formData.name.trim() || 
                    !formData.phone.trim() || 
                    !formData.email.trim() || 
                    !formData.selectedDateTime || 
                    crmEndpointFailed || 
                    calendarVerificationFailed
                  ) ? '#999' : '#cb4f2e',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  padding: '18px',
                  borderRadius: 50,
                  border: 'none',
                  cursor: (
                    submitting || 
                    !formData.name.trim() || 
                    !formData.phone.trim() || 
                    !formData.email.trim() || 
                    !formData.selectedDateTime || 
                    crmEndpointFailed || 
                    calendarVerificationFailed
                  ) ? 'not-allowed' : 'pointer',
                  boxShadow: (
                    submitting || 
                    !formData.name.trim() || 
                    !formData.phone.trim() || 
                    !formData.email.trim() || 
                    !formData.selectedDateTime || 
                    crmEndpointFailed || 
                    calendarVerificationFailed
                  ) ? 'none' : '0 4px 18px rgba(203,79,46,0.35)',
                  transition: 'background 0.2s',
                  opacity: (
                    submitting || 
                    !formData.name.trim() || 
                    !formData.phone.trim() || 
                    !formData.email.trim() || 
                    !formData.selectedDateTime || 
                    crmEndpointFailed || 
                    calendarVerificationFailed
                  ) ? 0.6 : 1,
                }}
                onMouseOver={(e) => {
                  if (
                    !submitting && 
                    formData.name.trim() && 
                    formData.phone.trim() && 
                    formData.email.trim() && 
                    formData.selectedDateTime && 
                    !crmEndpointFailed && 
                    !calendarVerificationFailed
                  ) {
                    e.currentTarget.style.background = '#a83c22';
                  }
                }}
                onMouseOut={(e) => {
                  if (
                    !submitting && 
                    formData.name.trim() && 
                    formData.phone.trim() && 
                    formData.email.trim() && 
                    formData.selectedDateTime && 
                    !crmEndpointFailed && 
                    !calendarVerificationFailed
                  ) {
                    e.currentTarget.style.background = '#cb4f2e';
                  }
                }}
              >
                {submitting ? 'Booking your consultation...' : 'Book Free Consultation'}
              </button>

              <p
                style={{
                  textAlign: 'center',
                  color: '#777',
                  fontSize: '0.85rem',
                  marginTop: 16,
                  marginBottom: 0,
                }}
              >
                By booking, you'll receive a confirmation email with your appointment details.
              </p>
            </form>

            <div
              style={{
                textAlign: 'center',
                color: '#777',
                fontSize: '0.9rem',
                fontStyle: 'italic',
              }}
            >
              Don't see a time that works?{' '}
              {isMobile ? (
                <a
                  href={`sms:${SMS_NUMBER}`}
                  onClick={handleSmsClick}
                  style={{
                    color: '#2d5986',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  Text us your availability
                </a>
              ) : (
                <button
                  onClick={() => { handleSmsClick(); setShowPhoneModal(true); }}
                  style={{
                    color: '#2d5986',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit',
                    fontStyle: 'inherit',
                  }}
                >
                  Text us your availability
                </button>
              )}
              .
            </div>
      </div>
    </>
  );
};

export default Booking;